import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import config from '../config'
import arraySort from 'array-sort'
import utils from '../utils'

Vue.use(Vuex)

const initialState = {
  posts: [],
  cursors: {},
  postFilter: '',
  postSort: '',
  isUnauthorizedPost: false,
  isPostFound: false
}

const getters = {
  cursors: state => state.cursors,
  communityPosts: state => id => state.posts.filter(post => post.community && post.community.id === id),
  filteredPosts: (state) => state.posts,
  postsFilter: state => state.postFilter,
  postsSort: state => state.postSort,
  post: state => id => state.posts.find(post => post.id === id)
}

const mutations = {
  posts: (state, posts) => {
    // Remove duplicates
    const list = posts.filter((post, index, postsArray) =>
      index === postsArray.findIndex((p) => (p.id === post.id))
    )
    if (state.postSort === 'chronology') {
      state.posts = arraySort(list, 'createdAt', { reverse: true })
    } else {
      state.posts = list
    }
  },
  cursors: (state, cursors) => {
    state.cursors = cursors
  },
  addPost: (state, post) => {
    state.posts.unshift(post)
  },
  updatePost: (state, post) => {
    const index = state.posts.findIndex((p) => (p.id === post.id))
    Vue.set(state.posts, index, post)
  },
  setPostFilter: (state, postFilter) => {
    state.postFilter = postFilter
  },
  setPostSort: (state, postSort) => {
    state.postSort = postSort
  },
  isUnauthorizedPost: (state, isUnauthorizedPost) => {
    state.isUnauthorizedPost = isUnauthorizedPost
  },
  isPostFound: (state, isPostFound) => {
    state.isPostFound = isPostFound
  },
  likePost: (state, { postId, type, userId }) => {
    const postIndex = state.posts.findIndex((post) => post.id === postId)
    Vue.set(state.posts[postIndex], 'userLike', { type: type })
    const likeIndex = state.posts[postIndex].likes.findIndex(like => like.idUser === userId)
    if (likeIndex !== -1) {
      // change like type
      Vue.set(state.posts[postIndex].likes, likeIndex, { idUser: userId, type: type })
    } else {
      // new like
      state.posts[postIndex].likes.push({ idUser: userId, type: type })
    }
  },
  unlikePost: (state, { postId, userId }) => {
    const postIndex = state.posts.findIndex((post) => post.id === postId)
    state.posts[postIndex].userLike = undefined
    const likeIndex = state.posts[postIndex].likes.findIndex(like => like.idUser === userId)
    if (likeIndex !== -1) {
      state.posts[postIndex].likes.splice(likeIndex, 1)
    }
  },
  bookmarkPost: (state, postIndex) => {
    state.posts[postIndex].bookmarked = !state.posts[postIndex].bookmarked
  },
  setIsCommentedByUser: (state, payload) => {
    state.posts[payload.postIndex].commented = isCommentedByUser(payload.postComments, payload.userId)
  },
  incrementCommentsCounts: (state, payload) => {
    state.posts[payload.postIndex].commentsCount = payload.postCommentsLength
  },
  incrementPostCommentsCounts: (state, payload) => {
    state.posts[payload.postIndex].commentsCount++
  },
  softDelete: (state, postId) => {
    const index = state.posts.findIndex(post => post.id === postId)
    state.posts.splice(index, 1)
  },
  unreportPost: (state, postId) => {
    const index = state.posts.findIndex((p) => p.id === postId)
    state.posts[index].reportCount = 0
  },
  updateReportedCommentsCount: (state, payload) => {
    state.posts[payload.postIndex].reportedCommentsCount = payload.reportedCommentsCount
  }
}

const actions = {
  async fetchPosts({ commit, state, getters, dispatch }) {
    let res = await fetchPostsWithFilter(state.postFilter)(state, getters)
    if (!res.data.cursors) {
      dispatch('resetPosts')
    }
    res.data.results.forEach(post => {
      dispatch('searchUsersByCommunity', post.community.id)
    })
    commit('posts', state.posts.concat(res.data.results))
    commit('cursors', res.data.cursors)
  },
  /**
   * Fetch a post and add it to the posts array
   */
  async fetchPost({ commit, dispatch }, postId) {
    try {
      let res = await axios.get(`${config.getRootApi()}/posts/${postId}`)
      if (res.status === 200) {
        commit('isUnauthorizedPost', false)
        commit('isPostFound', true)
        commit('addPost', res.data)
        dispatch('searchUsersByCommunity', res.data.community.id)
      }
    } catch (error) {
      commit('isUnauthorizedPost', true)
      commit('isPostFound', error.response.status === 403)
    }
  },
  async softDelete({ commit, dispatch, getters }, postId) {
    let res = await axios.delete(`${config.getRootApi()}/posts/${postId}`)
    if (res.status === 200) {
      commit('softDelete', postId)
      commit('decrementPostCount')
      commit('decrementCountPublishedPosts')
      dispatch('fetchCommunityDocuments', getters.communityDetail.community.id)
    }
  },
  async softDeleteReportedPost({ commit }, postId) {
    return axios.delete(`${config.getRootApi()}/admin/contents/posts/${postId}`, null)
      .then(res => {
        if (res.status === 200) {
          commit('softDelete', postId)
          return true
        } else {
          return false
        }
      }).catch(() => {
        return false
      })
  },
  async unreportPost({ commit }, post) {
    return axios.post(`${config.getRootApi()}/admin/contents/posts/${post.id}/unreport`, null)
      .then(response => {
        if (response.status === 200) {
          if (post.reportedCommentsCount === 0) {
            commit('softDelete', post.id)
          } else {
            commit('unreportPost', post.id)
          }
          commit('decrementCountReportedContent')
          return true
        } else {
          return false
        }
      }).catch(() => {
        return false
      })
  },
  async fetchCommunityPosts({ commit, state, getters, rootState, dispatch }, communityId) {
    let res = await fetchCommunityPostsWithFilter(state.postFilter)({ state, rootState }, communityId)
    if (!res.data.cursors) {
      dispatch('resetPosts')
    }
    commit('posts', state.posts.concat(res.data.results))
    commit('cursors', res.data.cursors)
  },
  async createPost({ commit, dispatch }, post) {
    return axios.post(`${config.getRootApi()}/posts`, post, {
      headers: {
        'content-type': 'multipart/form-data'
      }
    })
      .then(result => {
        if (result.status === 201) {
          commit('addPost', result.data)
          commit('incrementPostCount')
          commit('incrementCountPublishedPosts')
          dispatch('fetchCommunityDocuments', result.data.community.id)
        }
        return result
      })
      .catch((err) => {
        return err
      })
  },
  async editPost({ commit, dispatch }, { postId, formData }) {
    return axios.post(`${config.getRootApi()}/posts/${postId}`, formData, {
      headers: {
        'content-type': 'multipart/form-data'
      }
    })
      .then(result => {
        if (result.status === 200) {
          commit('updatePost', result.data)
          dispatch('fetchCommunityDocuments', result.data.community.id)
        }
        return result
      })
      .catch((err) => {
        return err
      })
  },
  async likePost({ commit, state, dispatch, rootState }, payload) {
    const res = await axios.post(`${config.getRootApi()}/posts/${payload.postId}/like`, { type: payload.type })
    if (res.status === 200) {
      commit('likePost', { ...payload, userId: rootState.user.user.id })
      commit('posts', state.posts)
    } else {
      console.error(res)
    }
  },
  async unlikePost({ commit, state, dispatch, rootState }, payload) {
    const res = await axios.post(`${config.getRootApi()}/posts/${payload.postId}/unlike`)
    if (res.status === 200) {
      commit('unlikePost', { ...payload, userId: rootState.user.user.id })
      commit('posts', state.posts)
    } else {
      console.error(res)
    }
  },
  async updateCommentsCounter({ commit, state, getters }, postId) {
    const postComments = getters.comments(postId)
    const userId = getters.currentUserId
    const index = state.posts.findIndex((post) => post.id === postId)
    if (index >= 0) {
      commit('setIsCommentedByUser', { postIndex: index, postComments: postComments, userId: userId })
      commit('incrementCommentsCounts', { postIndex: index, postCommentsLength: getPostCommentsLength(postComments) })
    }
    commit('posts', state.posts)
  },
  async incrementCommentsCounter({ commit, state, getters }, postId) {
    const postComments = getters.comments(postId)
    const userId = getters.currentUserId
    const index = state.posts.findIndex((post) => post.id === postId)
    if (index >= 0) {
      commit('setIsCommentedByUser', { postIndex: index, postComments: postComments, userId: userId })
      commit('incrementPostCommentsCounts', { postIndex: index })
    }
    commit('posts', state.posts)
  },
  async bookmarkPost({ commit, state }, postId) {
    const res = await axios.post(`${config.getRootApi()}/posts/${postId}/bookmark`, null)
    if (res.status === 200) {
      const index = state.posts.findIndex((post) => post.id === postId)
      commit('bookmarkPost', index)
      commit('posts', state.posts)
    } else {
      console.error(res)
    }
  },
  async unbookmarkPost({ commit, state }, postId) {
    const res = await axios.post(`${config.getRootApi()}/posts/${postId}/unbookmark`, null)
    if (res.status === 200) {
      const index = state.posts.findIndex((post) => post.id === postId)
      commit('bookmarkPost', index)
      commit('posts', state.posts)
    } else {
      console.error(res)
    }
  },
  async report({ commit }, postId) {
    const res = await axios.post(`${config.getRootApi()}/posts/${postId}/report`, null)
    if (res.status !== 200) {
      console.error(res)
    }
  },
  resetPosts({ commit }) {
    commit('cursors', {})
    commit('posts', [])
  },
  async setPostFilter({ commit, dispatch }, filter) {
    // Reset filter to force refresh of computed values depending on filter
    // This way, the user can click on an already selected filter to refresh the page
    commit('setPostFilter', '')
    await dispatch('resetPosts')
    await dispatch('resetSortPosts')
    commit('setPostFilter', filter)
  },
  resetPostFilter({ commit }) {
    commit('setPostFilter', 'all')
  },
  async sortPosts({ commit, dispatch, getters }, sortBy) {
    commit('setPostSort', sortBy)
    dispatch('resetPosts')
    if (getters.activeRoute.name === 'communityPage') {
      let communityId = getters.communityDetail.community.id
      dispatch('fetchCommunityPosts', communityId)
    } else {
      dispatch('fetchPosts')
    }
  },
  resetSortPosts({ commit }) {
    commit('setPostSort', 'chronology')
  },
  async updateReportedCommentsCount({ commit, state }, postId) {
    axios.get(`${config.getRootApi()}/admin/contents/posts/${postId}/reportedCount`)
      .then((response) => {
        if (response.status === 200) {
          const count = response.data.count
          const index = state.posts.findIndex((p) => p.id === postId)
          if (count === 0 && state.posts[index].reportCount === 0) {
            commit('softDelete', postId)
          } else {
            commit('updateReportedCommentsCount', { postIndex: index, reportedCommentsCount: count })
          }
        }
      })
  }
}

const fetchPostsWithFilter = (filter) => {
  if (filter === 'myPublished') {
    return fetchCurrentUserPosts
  } else if (filter === 'reported') {
    return fetchReportedContents
  }
  return fetchAllPosts
}

const fetchCommunityPostsWithFilter = (filter) => {
  if (filter === 'myPublished') {
    return fetchCurrentUserPostsByCommunity
  }
  return fetchAllCommunityPosts
}

const fetchAllPosts = (state) => {
  return fetchPosts(state, undefined, undefined)
}

const fetchReportedContents = (state) => {
  return axios.get(`${config.getRootApi()}/admin/contents/reported`, {
    params: utils.getFetchParams(state)
  })
}

const fetchCurrentUserPosts = (state, getters) => {
  return fetchPosts(state, getters.currentUserId, undefined)
}

const fetchAllCommunityPosts = ({ state }, communityId) => {
  return fetchPosts(state, undefined, communityId)
}

const fetchCurrentUserPostsByCommunity = ({ state, rootState }, communityId) => {
  return fetchPosts(state, rootState.security.currentUserId, communityId)
}

const fetchPosts = (state, userId, communityId) => {
  const params = utils.getFetchParams(state)
  params.userId = userId
  params.communityId = communityId
  return axios.get(`${config.getRootApi()}/posts`, { params: params })
}

/**
 * Return true if current user commented this post
 */
const isCommentedByUser = (commentMap, userId) => {
  for (let comment of Object.values(commentMap)) {
    if (comment.idUser === userId) {
      return true
    } else {
      for (let child of comment.children) {
        if (child.idUser === userId) {
          return true
        }
      }
    }
  }
  return false
}

const getPostCommentsLength = (commentMap) => {
  let commentsCount = 0
  for (let comment of Object.values(commentMap)) {
    commentsCount += comment.children.length
    commentsCount++
  }
  return commentsCount
}

export default {
  state: initialState,
  getters,
  mutations,
  actions
}
